import { BookingForm, BookingFormStatus } from '../interfaces/booking-form.interface';

/**
 * Actions a supplier can take from the read-only booking-form view, keyed to a handler in
 * `BookingFormsViewComponent.onAction`.
 *
 * `delete` (MP-10980) is offered on Draft and Issued only — a Signed form is locked and must
 * never be deletable (the gateway also enforces this). A Signed form can only be `cancel`led
 * (MP-11052), which keeps it for good as a Cancelled record.
 *
 * `createNew` (MP-11308) is offered on a Cancelled form, so it's clear where to make the next one. The view
 * hides it while the job already has a Draft, Issued or Signed form.
 */
export type StripActionId = 'issue' | 'edit' | 'share' | 'downloadPdf' | 'delete' | 'cancel' | 'createNew';

export interface StripAction {
  id: StripActionId;
  label: string;
  color: 'cta' | 'secondary' | 'destructive';
  /** `ate-icon` name shown before the label (MP-11316). */
  icon?: string;
}

const EDIT: StripAction = { id: 'edit', label: 'Edit Booking Form', color: 'secondary', icon: 'edit-v3' };
const DELETE: StripAction = { id: 'delete', label: 'Delete Booking Form', color: 'destructive', icon: 'bin-v3' };
const SHARE: StripAction = { id: 'share', label: 'Share Booking Form', color: 'cta' };
const DOWNLOAD_PDF: StripAction = { id: 'downloadPdf', label: 'Download PDF', color: 'secondary', icon: 'download' };

export interface StatusStripConfig {
  /** Pill background/text tone; maps to Tailwind classes in the template. */
  pillTone: 'slate' | 'blue' | 'green' | 'red';
  /** Message-box tone: 'warning' (caution — not live yet), 'info' (neutral), 'success' (done/locked). */
  messageTone: 'warning' | 'info' | 'success';
  /** Headline text, computed from the form (e.g. reference number when issued). */
  headline: (bookingForm: BookingForm) => string;
  /** Optional secondary line under the headline. */
  subtext?: string;
  /** Body copy explaining what state the form is in and what the supplier can do. */
  message: string;
  /** Action buttons, in display order. */
  actions: StripAction[];
}

/**
 * The status strip shown over the read-only preview for each booking-form state.
 *
 * - Draft  → MP-10679: issue, edit or delete (no customer can see it yet)
 * - Issued → MP-10684: share the public link, download, edit until signed, or delete
 * - Signed → MP-10685: locked; share the signed link, download, or cancel (MP-11052)
 * - Cancelled → MP-11052: kept for good as a record; create a new form (MP-11308) or download
 *
 * Each list leads with its main CTA; the rest are downgraded to outline buttons, with icons on Download PDF,
 * Edit and Delete (MP-11316).
 */
export const BOOKING_FORM_STATUS_STRIP: Record<BookingFormStatus, StatusStripConfig> = {
  Draft: {
    pillTone: 'slate',
    messageTone: 'warning',
    headline: () => 'Draft Booking Form',
    subtext: 'No reference assigned yet',
    message: 'This is a draft booking form and cannot be filled out by a customer yet. Please check the form and send it to your customer.',
    actions: [{ id: 'issue', label: 'Send to customer', color: 'cta' }, EDIT, DELETE]
  },
  Issued: {
    pillTone: 'blue',
    messageTone: 'info',
    headline: bookingForm => `Booking Form: ${bookingForm.reference ?? ''}`.trim(),
    message: 'This booking form can be shared with your customer. You can edit it until they have signed the form.',
    actions: [SHARE, DOWNLOAD_PDF, EDIT, DELETE]
  },
  Signed: {
    pillTone: 'green',
    messageTone: 'success',
    headline: bookingForm => `Booking Form: ${bookingForm.reference ?? ''}`.trim(),
    message: 'Your customer has signed this form. You can no longer edit it.',
    actions: [SHARE, DOWNLOAD_PDF, { id: 'cancel', label: 'Cancel Booking Form', color: 'secondary' }]
  },
  Cancelled: {
    pillTone: 'red',
    messageTone: 'info',
    headline: bookingForm => `Booking Form: ${bookingForm.reference ?? ''}`.trim(),
    message: 'This booking form has been cancelled. It is kept as a record and can no longer be changed or signed.',
    actions: [{ id: 'createNew', label: 'Create New Booking Form', color: 'cta' }, DOWNLOAD_PDF]
  }
};

/** Tailwind classes for the status pill, by tone. */
export const STATUS_PILL_CLASS: Record<StatusStripConfig['pillTone'], string> = {
  slate: 'tw-bg-slate-100 tw-text-slate-700',
  blue: 'tw-bg-blue-100 tw-text-blue-800',
  green: 'tw-bg-green-100 tw-text-green-700',
  red: 'tw-bg-red-100 tw-text-red-700'
};

/** Tailwind classes for the message box, by tone. */
export const MESSAGE_TONE_CLASS: Record<StatusStripConfig['messageTone'], string> = {
  warning: 'tw-border-yellow-200 tw-bg-yellow-50 tw-text-yellow-800',
  info: 'tw-border-slate-200 tw-bg-slate-50 tw-text-slate-700',
  success: 'tw-border-green-200 tw-bg-green-50 tw-text-green-700'
};

/** Icon colour for the message box, by tone. */
export const MESSAGE_TONE_ICON_CLASS: Record<StatusStripConfig['messageTone'], string> = {
  warning: 'tw-text-yellow-600',
  info: 'tw-text-slate-500',
  success: 'tw-text-green-600'
};
